import { useRef } from 'react';

import FORM_KEYS from 'apps/web/constants/forms/lesson-plan/formKeys';
import useAutosizeTextArea from 'apps/web/hooks/useAutoSizeTextArea';
import { useScrollToError } from 'apps/web/hooks/useScrollToError';
import processInput from 'apps/web/utils/formInput';
import {
  shouldDisableObjective2,
  shouldDisableObjective3,
} from 'apps/web/utils/lesson-format/objectiveFieldHelpers';

import {
  LP_PAGE_OBJECTIVE_CHAR_LIMIT,
  LP_PAGE_SUBJECT_CHAR_LIMIT,
} from '@lessonPlanner/constants';

import { SelectField, TextAreaField, TextField } from '../form-fields';

export default function MontessoriLP({
  errors,
  // formContents,
  watchedValues,
  triggerValidation,
  setValue,
}: {
  errors;
  // formContents;
  watchedValues;
  triggerValidation;
  setValue;
}) {
  const updateFieldValue = async (fieldName, value) => {
    setValue(fieldName, value);
  };

  const textAreaRefs = {
    objective: useRef<HTMLTextAreaElement>(null),
    objective2: useRef<HTMLTextAreaElement>(null),
    objective3: useRef<HTMLTextAreaElement>(null),
    keyArea: useRef<HTMLTextAreaElement>(null),
    keyArea2: useRef<HTMLTextAreaElement>(null),
    keyArea3: useRef<HTMLTextAreaElement>(null),
  };

  useAutosizeTextArea(textAreaRefs.objective.current, watchedValues.objective);
  useAutosizeTextArea(
    textAreaRefs.objective2.current,
    watchedValues.objective2,
  );
  useAutosizeTextArea(
    textAreaRefs.objective3.current,
    watchedValues.objective3,
  );

  useAutosizeTextArea(textAreaRefs.keyArea.current, watchedValues.keyArea);
  useAutosizeTextArea(textAreaRefs.keyArea2.current, watchedValues.keyArea2);
  useAutosizeTextArea(textAreaRefs.keyArea3.current, watchedValues.keyArea3);

  useScrollToError(errors);

  return (
    <>
      <TextField
        className="col-span-full"
        errorMessage={!!errors.title && 'Please enter the Title'}
        id={FORM_KEYS.title}
        label="Title"
        name={FORM_KEYS.title}
        placeholder="Input the subject for this field."
        type="text"
        value={watchedValues.title}
        onBlur={() => {
          processInput(
            updateFieldValue,
            triggerValidation,
            FORM_KEYS.title,
            watchedValues.title,
          );
        }}
        onChange={(e) => {
          processInput(
            updateFieldValue,
            triggerValidation,
            FORM_KEYS.title,
            e.target.value,
          );
        }}
      />

      <TextField
        className="col-span-full"
        id={FORM_KEYS.classProgram}
        label="Class Program"
        maxLength={LP_PAGE_SUBJECT_CHAR_LIMIT}
        name={FORM_KEYS.classProgram}
        placeholder="Input the class program."
        type="text"
        value={watchedValues.classProgram}
        errorMessage={
          !!errors.classProgram && 'Please enter the class program.'
        }
        isMaxLengthReached={
          watchedValues.classProgram?.length >= LP_PAGE_SUBJECT_CHAR_LIMIT
        }
        onBlur={() => {
          processInput(
            updateFieldValue,
            triggerValidation,
            FORM_KEYS.classProgram,
            watchedValues.classProgram?.trim() || '',
          );
        }}
        onChange={(e) => {
          processInput(
            updateFieldValue,
            triggerValidation,
            FORM_KEYS.classProgram,
            e.target.value,
          );
        }}
      />

      <TextField
        className="col-span-full"
        id={FORM_KEYS.prerequisites}
        label="Prerequisites"
        maxLength={LP_PAGE_OBJECTIVE_CHAR_LIMIT}
        name={FORM_KEYS.prerequisites}
        placeholder="Input the prerequisites."
        type="text"
        value={watchedValues.prerequisites}
        errorMessage={
          !!errors.prerequisites && 'Please enter the prerequisites.'
        }
        isMaxLengthReached={
          watchedValues.prerequisites?.length >= LP_PAGE_OBJECTIVE_CHAR_LIMIT
        }
        onBlur={() => {
          processInput(
            updateFieldValue,
            triggerValidation,
            FORM_KEYS.prerequisites,
            watchedValues.prerequisites?.trim() || '',
          );
        }}
        onChange={(e) => {
          processInput(
            updateFieldValue,
            triggerValidation,
            FORM_KEYS.prerequisites,
            e.target.value,
          );
        }}
      />

      <SelectField
        className="col-span-full"
        error={!!errors.materials}
        id="montessoriMaterials"
        label="Montessori Materials"
        name="montessoriMaterials"
        updateFn={updateFieldValue}
        validateFn={triggerValidation}
        value={watchedValues.materials}
        errorMessage={
          !!errors.materials && 'Please select the Montessori Materials'
        }
      >
        {['none', 'common-classroom-materials', 'any-materials-available'].map(
          (material, index) => (
            <option key={index} value={material}>
              {material
                .replace(/-/g, ' ')
                .replace(/\b\w/g, (char) => char.toUpperCase())}
            </option>
          ),
        )}
      </SelectField>

      <div className="col-span-full">
        <TextAreaField
          showSubLabel
          className="col-span-full"
          error={!!errors.objective}
          errorMessage={!!errors.objective && 'Please enter the Objective'}
          id={FORM_KEYS.objective}
          label="Objectives"
          maxLength={LP_PAGE_OBJECTIVE_CHAR_LIMIT}
          name={FORM_KEYS.objective}
          placeholder="Input the first objective for this lesson plan"
          rows={2}
          subLabel={'At the end of the lesson, the students will be able to...'}
          textAreaRef={textAreaRefs.objective}
          value={watchedValues.objective}
          isMaxLengthReached={
            watchedValues.objective?.length >= LP_PAGE_OBJECTIVE_CHAR_LIMIT
          }
          onBlur={() => {
            processInput(
              updateFieldValue,
              triggerValidation,
              FORM_KEYS.objective,
              watchedValues.objective?.trim() || '',
            );
          }}
          onChange={(e) => {
            processInput(
              updateFieldValue,
              triggerValidation,
              FORM_KEYS.objective,
              e.target.value,
            );
          }}
        />

        <TextAreaField
          showSubLabel
          className="col-span-full"
          disabled={shouldDisableObjective2(watchedValues)}
          error={!!errors.objective2}
          id={FORM_KEYS.objective2}
          maxLength={LP_PAGE_OBJECTIVE_CHAR_LIMIT}
          name={FORM_KEYS.objective2}
          placeholder="Input the second objective for this lesson plan"
          rows={2}
          textAreaRef={textAreaRefs.objective2}
          value={watchedValues.objective2}
          isMaxLengthReached={
            watchedValues.objective2?.length >= LP_PAGE_OBJECTIVE_CHAR_LIMIT
          }
          onBlur={() => {
            processInput(
              updateFieldValue,
              triggerValidation,
              FORM_KEYS.objective2,
              watchedValues.objective2?.trim() || '',
            );
          }}
          onChange={(e) => {
            processInput(
              updateFieldValue,
              triggerValidation,
              FORM_KEYS.objective2,
              e.target.value,
            );
          }}
        />

        <TextAreaField
          showSubLabel
          className="col-span-full"
          disabled={shouldDisableObjective3(watchedValues)}
          error={!!errors.objective3}
          id={FORM_KEYS.objective3}
          maxLength={LP_PAGE_OBJECTIVE_CHAR_LIMIT}
          name={FORM_KEYS.objective3}
          placeholder="Input the third objective for this lesson plan"
          rows={2}
          textAreaRef={textAreaRefs.objective3}
          value={watchedValues.objective3}
          isMaxLengthReached={
            watchedValues.objective3?.length >= LP_PAGE_OBJECTIVE_CHAR_LIMIT
          }
          onBlur={() => {
            processInput(
              updateFieldValue,
              triggerValidation,
              FORM_KEYS.objective3,
              watchedValues.objective3?.trim() || '',
            );
          }}
          onChange={(e) => {
            processInput(
              updateFieldValue,
              triggerValidation,
              FORM_KEYS.objective3,
              e.target.value,
            );
          }}
        />
      </div>

      <div className="col-span-full">
        <TextAreaField
          showSubLabel
          className="col-span-full"
          error={!!errors.keyArea}
          errorMessage={!!errors.keyArea && 'Please enter the key area'}
          id={FORM_KEYS.keyArea}
          label="Key Area"
          maxLength={LP_PAGE_OBJECTIVE_CHAR_LIMIT}
          name={FORM_KEYS.keyArea}
          placeholder="Input the key area of focus"
          rows={2}
          subLabel={'Include topics to emphasize...'}
          textAreaRef={textAreaRefs.keyArea}
          value={watchedValues.keyArea}
          isMaxLengthReached={
            watchedValues.keyArea?.length >= LP_PAGE_OBJECTIVE_CHAR_LIMIT
          }
          onBlur={() => {
            processInput(
              updateFieldValue,
              triggerValidation,
              FORM_KEYS.keyArea,
              watchedValues.keyArea?.trim() || '',
            );
          }}
          onChange={(e) => {
            processInput(
              updateFieldValue,
              triggerValidation,
              FORM_KEYS.keyArea,
              e.target.value,
            );
          }}
        />
        {/* make keyArea2 and keyArea3 */}

        <TextAreaField
          showSubLabel
          className="col-span-full"
          error={!!errors.keyArea2}
          id={FORM_KEYS.keyArea2}
          maxLength={LP_PAGE_OBJECTIVE_CHAR_LIMIT}
          name={FORM_KEYS.keyArea2}
          placeholder="Input the second key area of focus"
          rows={2}
          textAreaRef={textAreaRefs.keyArea2}
          value={watchedValues.keyArea2}
          isMaxLengthReached={
            watchedValues.keyArea2?.length >= LP_PAGE_OBJECTIVE_CHAR_LIMIT
          }
          onBlur={() => {
            processInput(
              updateFieldValue,
              triggerValidation,
              FORM_KEYS.keyArea2,
              watchedValues.keyArea2?.trim() || '',
            );
          }}
          onChange={(e) => {
            processInput(
              updateFieldValue,
              triggerValidation,
              FORM_KEYS.keyArea2,
              e.target.value,
            );
          }}
        />

        <TextAreaField
          showSubLabel
          className="col-span-full"
          error={!!errors.keyArea3}
          id={FORM_KEYS.keyArea3}
          maxLength={LP_PAGE_OBJECTIVE_CHAR_LIMIT}
          name={FORM_KEYS.keyArea3}
          placeholder="Input the third key area of focus"
          rows={2}
          textAreaRef={textAreaRefs.keyArea3}
          value={watchedValues.keyArea3}
          isMaxLengthReached={
            watchedValues.keyArea3?.length >= LP_PAGE_OBJECTIVE_CHAR_LIMIT
          }
          onBlur={() => {
            processInput(
              updateFieldValue,
              triggerValidation,
              FORM_KEYS.keyArea3,
              watchedValues.keyArea3?.trim() || '',
            );
          }}
          onChange={(e) => {
            processInput(
              updateFieldValue,
              triggerValidation,
              FORM_KEYS.keyArea3,
              e.target.value,
            );
          }}
        />
      </div>
    </>
  );
}
